"use client";

import dynamic from "next/dynamic";
import { buttonClass } from "@/components/ui/button";
import type { Locale } from "@/lib/i18n/config";
import enCommon from "@/locales/en/common.json";
import idCommon from "@/locales/id/common.json";

// ssr: false keeps @sentry/nextjs out of the OpenNext server bundle — see
// src/components/error/crash-reporter.tsx.
const CrashReporter = dynamic(
	() => import("@/components/error/crash-reporter"),
	{ ssr: false },
);

const DICTIONARY: Record<Locale, typeof enCommon> = {
	en: enCommon,
	id: idCommon,
};

// Every clickable element here that isn't a native `<button>` uses this
// exact ring pattern (src/components/CLAUDE.md → "Required focus-visible
// pattern"). Plain `<a>`, never `next/link` — `global-error.tsx` renders
// outside the router context (it replaces the whole app), so `<Link>` isn't
// safe there; using the same tag in both call sites keeps this component
// identical for the nested and root boundary.
const FOCUS_RING =
	"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

export type CrashScreenProps = {
	/** Active locale, detected by the caller (see `useClientLocale`). */
	locale: Locale;
	error: Error & { digest?: string };
	reset: () => void;
};

/**
 * Shared body for both error boundaries — `src/app/[locale]/error.tsx`
 * (nested, segment-level crash) and `src/app/global-error.tsx` (root
 * boundary, replaces the entire app). Renders only the content; each caller
 * owns its own document shell (global-error.tsx supplies `<html>`/`<body>`
 * itself since it has no parent layout).
 */
export function CrashScreen({ locale, error, reset }: CrashScreenProps) {
	const t = DICTIONARY[locale];

	function handleReportCrash() {
		const subject = encodeURIComponent("Crash Report");
		const body = encodeURIComponent(
			`Error: ${error.message}\nError ID: ${error.digest ?? "unknown"}\nURL: ${window.location.href}`,
		);
		window.location.href = `mailto:hello@lokerdollar.com?subject=${subject}&body=${body}`;
	}

	return (
		<div className="flex min-h-[80vh] items-center justify-center px-6 py-16">
			<CrashReporter error={error} />
			<div className="mx-auto w-full max-w-md space-y-6 text-center">
				{/* Icon */}
				<div className="flex justify-center">
					<div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-destructive/10">
						<svg
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth={1.5}
							className="h-10 w-10 text-destructive"
							aria-hidden="true"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
							/>
						</svg>
					</div>
				</div>

				{/* Status accent — decorative, not a real HTTP status */}
				<p
					className="text-9xl font-black tracking-tight text-foreground/[0.05]"
					aria-hidden="true"
				>
					500
				</p>

				{/* Heading */}
				<div className="-mt-2 space-y-2">
					<h1 className="text-2xl font-bold tracking-tight text-foreground">
						{t.crashHeading}
					</h1>
				</div>

				{/* Description — what happened (heading) -> state -> next step */}
				<p className="text-sm leading-relaxed text-muted-foreground">
					{t.crashBody}
				</p>

				{/* Error digest */}
				{error.digest && (
					<div className="rounded-lg border border-border bg-muted/50 px-4 py-2.5">
						<p className="font-mono text-xs text-muted-foreground">
							{t.crashErrorIdLabel}:{" "}
							<span className="select-all font-medium text-foreground">
								{error.digest}
							</span>
						</p>
					</div>
				)}

				{/* Primary actions */}
				<div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
					<button
						type="button"
						onClick={reset}
						className={buttonClass({ size: "lg", shape: "xl" })}
					>
						<svg
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth={2}
							className="h-4 w-4"
							aria-hidden="true"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
							/>
						</svg>
						{t.crashTryAgainCta}
					</button>
					<a
						href={`/${locale}`}
						className={`inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-border px-6 py-3 text-sm font-medium text-muted-foreground transition-colors duration-150 hover:bg-muted/50 hover:text-foreground ${FOCUS_RING}`}
					>
						<svg
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth={2}
							className="h-4 w-4"
							aria-hidden="true"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
							/>
						</svg>
						{t.crashBackHomeCta}
					</a>
				</div>

				{/* Secondary actions */}
				<div className="flex items-center justify-center gap-1">
					<button
						type="button"
						onClick={handleReportCrash}
						className={`inline-flex cursor-pointer items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground ${FOCUS_RING}`}
					>
						<svg
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth={1.75}
							className="h-3.5 w-3.5"
							aria-hidden="true"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M3 3v1.5M3 21v-6m0 0 2.77-.693a9 9 0 0 1 6.208.682l.108.054a9 9 0 0 0 6.086.71l3.114-.732a48.524 48.524 0 0 1-.005-10.499l-3.11.732a9 9 0 0 1-6.085-.711l-.108-.054a9 9 0 0 0-6.208-.682L3 9m0 6V9"
							/>
						</svg>
						{t.crashReportCta}
					</button>
					<span className="text-border" aria-hidden="true">
						·
					</span>
					<a
						href="mailto:hello@lokerdollar.com?subject=Support%20Request"
						className={`inline-flex cursor-pointer items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground ${FOCUS_RING}`}
					>
						<svg
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth={1.75}
							className="h-3.5 w-3.5"
							aria-hidden="true"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
							/>
						</svg>
						{t.crashContactCta}
					</a>
				</div>
			</div>
		</div>
	);
}
